/* ============================================================
   STRUKT — effects.js
   Premium motion layer: scroll progress, word-split headline
   reveals, 3D card tilt + shine, magnetic buttons, click ripple,
   scroll-velocity marquee skew, hero Ken Burns handled in CSS.
   Fully disabled for prefers-reduced-motion or when animations
   are switched off in theme settings.
   ============================================================ */
(function () {
  'use strict';

  var doc = document;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var animationsOff = window.theme && window.theme.animations === false;
  if (reducedMotion || animationsOff) return;

  doc.documentElement.classList.add('fx');

  function qs(sel, ctx) { return (ctx || doc).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }

  /* ---------- scroll progress bar ---------- */
  (function () {
    var bar = doc.createElement('div');
    bar.className = 'scroll-progress';
    bar.setAttribute('aria-hidden', 'true');
    doc.body.appendChild(bar);
    var raf = null;
    function update() {
      var max = doc.documentElement.scrollHeight - window.innerHeight;
      bar.style.transform = 'scaleX(' + (max > 0 ? Math.min(1, window.scrollY / max) : 0) + ')';
      raf = null;
    }
    window.addEventListener('scroll', function () {
      if (!raf) raf = requestAnimationFrame(update);
    }, { passive: true });
    update();
  })();

  /* ---------- word/letter-split headline reveals ---------- */
  (function () {
    var SELECTORS = [
      '.hero__heading', '.section-head__title', '.brand-statement__heading',
      '.newsletter-section__heading', '.bundle-promo__title', '.featured-product__title',
      '.collection-page__title', '.page-hero__heading', '.static-page__title', '.page-404__title'
    ].join(',');

    var targets = qsa(SELECTORS).filter(function (el) { return !el.dataset.split; });
    targets.forEach(function (el) {
      var text = el.textContent;
      el.dataset.split = '1';
      el.setAttribute('aria-label', text.trim());
      var words = text.split(/(\s+)/);
      el.textContent = '';
      words.forEach(function (word) {
        if (/^\s+$/.test(word)) {
          el.appendChild(doc.createTextNode(' '));
        } else if (word.length) {
          var mask = doc.createElement('span');
          mask.className = 'split-mask';
          mask.setAttribute('aria-hidden', 'true');
          var inner = doc.createElement('span');
          inner.className = 'split-word';
          inner.textContent = word;
          mask.appendChild(inner);
          el.appendChild(mask);
        }
      });
      var pieces = qsa('.split-word', el);
      var step = 70;
      pieces.forEach(function (w, i) {
        w.style.transitionDelay = (i * step) + 'ms';
      });
    });

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-split-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-split-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    targets.forEach(function (el) { io.observe(el); });
  })();

  /* ---------- 3D tilt + shine on cards ---------- */
  (function () {
    if (!window.matchMedia('(pointer: fine)').matches) return;
    var MAX = 7; // degrees
    qsa('.product-card__media, .category-tile__media, .routine-step__media').forEach(function (card) {
      card.classList.add('tilt');
      var raf = null;
      card.addEventListener('pointermove', function (e) {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          var r = card.getBoundingClientRect();
          var px = (e.clientX - r.left) / r.width;
          var py = (e.clientY - r.top) / r.height;
          card.style.setProperty('--tilt-x', ((py - 0.5) * -MAX).toFixed(2) + 'deg');
          card.style.setProperty('--tilt-y', ((px - 0.5) * MAX).toFixed(2) + 'deg');
          card.style.setProperty('--shine-x', (px * 100).toFixed(1) + '%');
          card.style.setProperty('--shine-y', (py * 100).toFixed(1) + '%');
          raf = null;
        });
      });
      card.addEventListener('pointerleave', function () {
        card.style.setProperty('--tilt-x', '0deg');
        card.style.setProperty('--tilt-y', '0deg');
      });
    });
  })();

  /* ---------- magnetic buttons ---------- */
  (function () {
    if (!window.matchMedia('(pointer: fine)').matches) return;
    var PULL = 0.28;
    qsa('.btn--lg, .hero .btn, .newsletter-form .btn').forEach(function (btn) {
      btn.classList.add('magnetic');
      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        var dx = e.clientX - (r.left + r.width / 2);
        var dy = e.clientY - (r.top + r.height / 2);
        btn.style.setProperty('--mag-x', (dx * PULL).toFixed(1) + 'px');
        btn.style.setProperty('--mag-y', (dy * PULL).toFixed(1) + 'px');
      });
      btn.addEventListener('pointerleave', function () {
        btn.style.setProperty('--mag-x', '0px');
        btn.style.setProperty('--mag-y', '0px');
      });
    });
  })();

  /* ---------- click ripple ---------- */
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest('.btn');
    if (!btn || btn.disabled) return;
    var r = btn.getBoundingClientRect();
    var ripple = doc.createElement('span');
    ripple.className = 'ripple';
    var size = Math.max(r.width, r.height) * 2.2;
    ripple.style.width = ripple.style.height = size + 'px';
    ripple.style.left = (e.clientX - r.left - size / 2) + 'px';
    ripple.style.top = (e.clientY - r.top - size / 2) + 'px';
    btn.appendChild(ripple);
    setTimeout(function () { ripple.remove(); }, 650);
  });

  /* ---------- scroll-velocity marquee skew ---------- */
  (function () {
    var tracks = qsa('[data-marquee], .marquee__track');
    if (!tracks.length) return;
    var lastY = window.scrollY;
    var skew = 0;
    var raf = null;
    function tick() {
      var y = window.scrollY;
      var v = y - lastY;
      lastY = y;
      var target = Math.max(-10, Math.min(10, v * 0.35));
      skew += (target - skew) * 0.12;
      if (Math.abs(skew) < 0.05 && Math.abs(target) < 0.05) {
        tracks.forEach(function (t) { t.style.setProperty('--marquee-skew', '0deg'); });
        raf = null;
        return;
      }
      tracks.forEach(function (t) { t.style.setProperty('--marquee-skew', skew.toFixed(2) + 'deg'); });
      raf = requestAnimationFrame(tick);
    }
    window.addEventListener('scroll', function () {
      if (!raf) raf = requestAnimationFrame(tick);
    }, { passive: true });
  })();

  /* ---------- giant watermark typography w/ parallax ---------- */
  (function () {
    var MAP = [
      { sel: '.featured-products', text: 'THE RANGE' },
      { sel: '.routine', text: 'ROUTINE' },
      { sel: '.icon-benefits', text: 'WHY STRUKT' },
      { sel: '.comparison', text: 'NO CONTEST' },
      { sel: '.ugc', text: 'THE MOVEMENT' }
    ];
    var marks = [];
    MAP.forEach(function (item) {
      var section = qs(item.sel);
      if (!section || qs('.watermark', section)) return;
      section.classList.add('has-watermark');
      var wm = doc.createElement('span');
      wm.className = 'watermark';
      wm.setAttribute('aria-hidden', 'true');
      wm.textContent = item.text;
      section.prepend(wm);
      marks.push({ el: wm, section: section });
    });

    qsa('.has-watermark').forEach(function (section) {
      if (marks.some(function (m) { return m.section === section; })) return;
      var wm = qs('.watermark', section);
      if (!wm) return;
      marks.push({ el: wm, section: section });
    });
    if (!marks.length) return;
    var raf = null;
    function update() {
      var vh = window.innerHeight;
      marks.forEach(function (m) {
        var r = m.section.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        var p = (vh - r.top) / (vh + r.height); // 0..1 through viewport
        m.el.style.setProperty('--wm-y', ((p - 0.5) * 90).toFixed(1) + 'px');
      });
      raf = null;
    }
    window.addEventListener('scroll', function () {
      if (!raf) raf = requestAnimationFrame(update);
    }, { passive: true });
    update();
  })();

  /* ---------- scroll-lit brand statement ---------- */
  (function () {
    var blocks = qsa('.brand-statement__text');
    if (!blocks.length) return;
    var lit = [];
    blocks.forEach(function (block) {
      qsa('p', block).forEach(function (p) {
        var words = p.textContent.split(/(\s+)/);
        p.textContent = '';
        words.forEach(function (w) {
          if (/^\s+$/.test(w)) { p.appendChild(doc.createTextNode(' ')); return; }
          if (!w.length) return;
          var span = doc.createElement('span');
          span.className = 'lit-word';
          span.textContent = w;
          p.appendChild(span);
          lit.push(span);
        });
      });
    });
    if (!lit.length) return;
    var raf = null;
    function update() {
      var vh = window.innerHeight;
      var first = lit[0].getBoundingClientRect();
      var last = lit[lit.length - 1].getBoundingClientRect();
      var start = vh * 0.85;
      var end = vh * 0.35;
      var top = first.top;
      var bottom = last.bottom;
      var total = Math.max(1, (bottom - top) + (start - end));
      var progress = Math.max(0, Math.min(1, (start - top) / total));
      var count = Math.round(progress * lit.length * 1.15);
      lit.forEach(function (span, i) { span.classList.toggle('is-lit', i < count); });
      raf = null;
    }
    window.addEventListener('scroll', function () {
      if (!raf) raf = requestAnimationFrame(update);
    }, { passive: true });
    update();
  })();

  /* ---------- hero scroll fade ---------- */
  (function () {
    var content = qs('.hero__content');
    if (!content) return;
    var raf = null;
    window.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        var h = window.innerHeight;
        var y = window.scrollY;
        if (y < h) {
          var p = y / h;
          content.style.opacity = (1 - p * 1.1).toFixed(3);
          content.style.transform = 'translateY(' + (p * 60).toFixed(1) + 'px)';
        }
        raf = null;
      });
    }, { passive: true });
  })();
})();
